import CustomButton from "Components/Custom/CustomButton";
import CustomInput from "Components/Custom/CustomInput";
import { useAppSelector } from "Components/Hooks/hooks";
import { useFormik } from "formik";
import React from "react";
import {
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";

const DEPOSIT_TYPES = ["Fixed", "Percentage"] as const;
export type DepositType = (typeof DEPOSIT_TYPES)[number];

interface AddDepositModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (
    type: DepositType,
    amount: number,
    paymentMethod: "cash" | "stripe",
  ) => void;
  grandTotal?: number;
  initialType?: DepositType;
  initialAmount?: number;
  initialPaymentMethod?: "cash" | "stripe";
}

interface FormValues {
  type: DepositType;
  amount: string;
  paymentMethod: "cash" | "stripe";
}

const AddDepositModal: React.FC<AddDepositModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  grandTotal = 0,
  initialType,
  initialAmount,
  initialPaymentMethod,
}) => {
  const user = useAppSelector((state) => state.auth.user);
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      type: initialType ?? "Fixed",
      amount: initialAmount != null ? String(initialAmount) : "",
      paymentMethod: initialPaymentMethod
        ? initialPaymentMethod
        : user?.stripe_account_status === "active"
        ? "stripe"
        : "cash",
    },
    validationSchema: Yup.object({
      amount: Yup.number()
        .typeError("Amount must be a number")
        .required("Amount is required")
        .min(0.01, "Amount must be greater than 0")
        .when("type", {
          is: "Percentage",
          then: (schema) => schema.max(100, "Percentage cannot exceed 100%"),
        }),
      paymentMethod: Yup.string().required("Payment method is required"),
    }),
    onSubmit: (values) => {
      onAdd(values.type, Number(values.amount), values.paymentMethod);
      formik.resetForm();
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const computedValue =
    formik.values.type === "Percentage" && formik.values.amount
      ? ((Number(formik.values.amount) / 100) * grandTotal).toFixed(2)
      : null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      contentClassName="br-8"
    >
      <ModalHeader className="bg-greyF5 p-3" toggle={handleClose}>
        Add Deposit
      </ModalHeader>

      <form
        className="h-100 d-flex flex-column justify-content-between"
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit();
        }}
      >
        <ModalBody className="p-3">
          {/* Type toggle */}
          <div className="mb-3">
            <Label className="font-primary fw-normal fs-14 text-black2D">
              Deposit Type
            </Label>
            <div
              className="d-flex align-items-center"
              style={{
                border: "2px solid #D1D5DB",
                borderRadius: "7px",
                padding: "3px",
                background: "#fff",
                height: "44px",
              }}
            >
              {DEPOSIT_TYPES.map((t) => {
                const isActive = formik.values.type === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      formik.setFieldValue("type", t);
                      formik.setFieldValue("amount", "");
                      formik.setFieldError("amount", undefined);
                    }}
                    className="border-0 d-flex align-items-center justify-content-center"
                    style={{
                      flex: 1,
                      height: "100%",
                      borderRadius: "5px",
                      background: isActive ? "#082B60" : "transparent",
                      color: isActive ? "#fff" : "#1F2937",
                      fontSize: "14px",
                      fontWeight: 500,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {t === "Fixed" ? "Fixed (£)" : "Percentage (%)"}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Amount input */}
          <div className="mb-3">
            <Label
              className="font-primary fw-normal fs-14 text-black2D"
              htmlFor="deposit-amount"
            >
              Amount
            </Label>
            <div className="position-relative">
              <div
                className="position-absolute start-0 top-0 p-2 ms-1 fs-15"
                style={{ marginTop: "2px", pointerEvents: "none", zIndex: 1 }}
              >
                {formik.values.type === "Fixed" ? "£" : "%"}
              </div>
              <Input
                id="deposit-amount"
                name="amount"
                type="number"
                placeholder={formik.values.type === "Fixed" ? "0.00" : "0"}
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                min={0}
                step={formik.values.type === "Fixed" ? "0.01" : "1"}
                className="auth-form-control ps-4"
                invalid={!!formik.touched.amount && !!formik.errors.amount}
              />
              {formik.touched.amount && formik.errors.amount && (
                <FormFeedback>{formik.errors.amount}</FormFeedback>
              )}
            </div>

            {/* Live computed value for percentage mode */}
            {computedValue && grandTotal > 0 && (
              <p className="text-grey89 fs-12 font-primary mt-1 mb-0">
                = £{computedValue} of £{grandTotal.toFixed(2)} grand total
              </p>
            )}
          </div>

          {/* Payment Method dropdown */}
          <CustomInput
            name="paymentMethod"
            label="Payment Method"
            type="select"
            value={formik.values.paymentMethod}
            onChange={(value) => {
              formik.handleChange(value);
            }}
            onBlur={formik.handleBlur}
            error={formik.errors.paymentMethod}
            touched={formik.touched.paymentMethod}
            dropdownOptions={[
              { label: "Cash", value: "cash" },
              { label: "Stripe", value: "stripe" },
            ]}
          />

          {formik.values.paymentMethod === "cash" && (
            <p className="fs-12 text-orangeFF fw-normal mt-n2 mb-3 font-primary">
              You are about to record a cash payment. Please ensure the amount
              has been physically received before proceeding.
            </p>
          )}

          {/* Actions */}
          <div className="d-flex align-items-center gap-2 pt-3 mt-2">
            <CustomButton
              text="Add Deposit"
              type="submit"
              className="bg-primary px-4 py-2"
            />
            <CustomButton
              text="Cancel"
              type="button"
              className="btn-light px-4 py-2"
              textColor="text-black2D"
              onClick={handleClose}
            />
          </div>
        </ModalBody>
      </form>
    </Modal>
  );
};

export default AddDepositModal;
